import { ScrollState } from '@/pages/welcome';
import { menu } from '@/routes';
import { Link } from '@inertiajs/react';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    Coffee,
    Croissant,
    CupSoda,
    IceCreamBowl,
    Pizza,
    Salad,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from './ui/button';

const Categories = [
    {
        id: 1,
        name: 'Hot Drinks',
        slug: 'hot-drink',
        icon: <Coffee size={38} />,
    },
    {
        id: 2,
        name: 'Cold Drinks',
        slug: 'cold-drink',
        icon: <CupSoda size={38} />,
    },
    {
        id: 3,
        name: 'Breakfast',
        slug: 'breakfast',
        icon: <Croissant size={38} />,
    },
    { id: 4, name: 'Lunch', slug: 'lunch', icon: <Pizza size={38} /> },
    {
        id: 5,
        name: 'Appetizers',
        slug: 'appetizers',
        icon: <Salad size={38} />,
    },
    {
        id: 6,
        name: 'Dessert',
        slug: 'dessert',
        icon: <IceCreamBowl size={38} />,
    },
];

interface Props {
    title?: string;
    description?: string;
    link?: string;
}

const ShopByCategory = ({
    title = 'Shop by Category',
    description,
    link,
}: Props) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [state, setState] = useState<ScrollState>({
        atStart: true,
        atEnd: false,
    });

    const updateScrollState = () => {
        const el = scrollRef.current;
        if (!el) return;

        setState({
            atStart: el.scrollLeft <= 0,
            atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1,
        });
    };

    const scrollToStart = () => {
        scrollRef.current?.scrollTo({
            left: 0,
            behavior: 'smooth',
        });
    };

    const scrollToEnd = () => {
        const el = scrollRef.current;
        if (!el) return;

        el.scrollTo({
            left: el.scrollWidth,
            behavior: 'smooth',
        });
    };

    return (
        <div className="container mx-auto px-6 py-10">
            {/* Header */}
            <div className="mb-4">
                <h2 className="text-2xl font-bold md:text-3xl text-primary">{title}</h2>
                {description && (
                    <p className="mt-1 text-gray-600">{description}</p>
                )}
            </div>

            {/* Actions */}
            <div className="mb-4 flex items-center">
                {link && (
                    <Button className="mt-2">
                        <Link href={link}>View All</Link>
                    </Button>
                )}

                <div className="ml-auto flex gap-2">
                    <button
                        disabled={state.atStart}
                        onClick={scrollToStart}
                        className={`rounded-full p-3 transition ${
                            state.atStart
                                ? 'cursor-not-allowed bg-gray-300'
                                : 'bg-primary hover:bg-primary/90'
                        }`}
                    >
                        <ChevronLeftIcon
                            className={`h-5 w-5 ${
                                state.atStart ? 'text-gray-500' : 'text-white'
                            }`}
                        />
                    </button>

                    <button
                        disabled={state.atEnd}
                        onClick={scrollToEnd}
                        className={`rounded-full p-3 transition ${
                            state.atEnd
                                ? 'cursor-not-allowed bg-gray-300'
                                : 'bg-primary hover:bg-primary/90'
                        }`}
                    >
                        <ChevronRightIcon
                            className={`h-5 w-5 ${
                                state.atEnd ? 'text-gray-500' : 'text-white'
                            }`}
                        />
                    </button>
                </div>
            </div>

            {/* Horizontal list */}
            <div
                ref={scrollRef}
                onScroll={updateScrollState}
                className="no-scrollbar flex gap-6 overflow-x-auto py-2"
            >
                {Categories.map((item) => (
                    <Link
                        key={item.id}
                        href={menu()}
                        data={{
                            type: item.slug,
                            category_id: undefined,
                        }}
                        className="group"
                    >
                        <div className="flex min-w-40 flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white p-6 text-center transition-all hover:border-primary hover:shadow-md">
                            {/* Icon */}
                            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-xl bg-gray-100 group-hover:bg-primary/10">
                                {item.icon}
                            </div>

                            {/* Name */}
                            <span className="font-medium text-gray-900">
                                {item.name}
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default ShopByCategory;
